/**
 * CategoriesPage - Main categories management page (WP-CAT-12)
 *
 * Top-level page for category management, replacing the Settings tab.
 * Provides 4 tabs:
 * - Manage: Enable/disable categories
 * - Create: Create/edit custom categories
 * - Rules: Categorization rules management
 * - Analytics: Spending by category
 *
 * URL parameter for tab selection: /categories?tab=manage
 */
import { useSearchParams } from 'react-router-dom';
import ManageCategories from './components/ManageCategories';
import CreateCategory from './components/CreateCategory';
import CategoryRules from './components/CategoryRules';
import SpendingAnalytics from './components/SpendingAnalytics';
import { cn } from '@/utils/cn';

// ============================================================================
// Types
// ============================================================================

type CategoriesTab = 'manage' | 'create' | 'rules' | 'analytics';

interface TabConfig {
  id: CategoriesTab;
  label: string;
  icon: React.ReactNode;
}

// ============================================================================
// Tab Configuration
// ============================================================================

const tabs: TabConfig[] = [
  {
    id: 'manage',
    label: 'Manage',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
      </svg>
    ),
  },
  {
    id: 'create',
    label: 'Create',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    ),
  },
  {
    id: 'rules',
    label: 'Rules',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
      </svg>
    ),
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
      </svg>
    ),
  },
];

// ============================================================================
// Component
// ============================================================================

/**
 * Parse tab from URL search params.
 */
function getTabFromParams(params: URLSearchParams): CategoriesTab {
  const tab = params.get('tab');
  if (tab && ['manage', 'create', 'rules', 'analytics'].includes(tab)) {
    return tab as CategoriesTab;
  }
  return 'manage';
}

/**
 * CategoriesPage - Main categories management page.
 *
 * Accessibility:
 * - Tab navigation uses proper ARIA roles (tablist, tab, tabpanel)
 * - Focus management when switching tabs
 * - Keyboard navigation support
 */
export default function CategoriesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = getTabFromParams(searchParams);

  // Handle tab change
  const handleTabChange = (tab: CategoriesTab) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    // Remove edit param when switching away from create tab
    if (tab !== 'create') {
      newParams.delete('edit');
    }
    setSearchParams(newParams, { replace: true });
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'manage':
        return <ManageCategories />;
      case 'create':
        return <CreateCategory />;
      case 'rules':
        return <CategoryRules />;
      case 'analytics':
        return <SpendingAnalytics />;
      default:
        return <ManageCategories />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Categories</h1>
        <p className="mt-1 text-gray-600">
          Manage transaction categories and categorization rules.
        </p>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav
          className="flex -mb-px space-x-8 overflow-x-auto"
          aria-label="Categories tabs"
          role="tablist"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={cn(
                'flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-t-md',
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`tabpanel-${tab.id}`}
              id={`tab-${tab.id}`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div
        role="tabpanel"
        id={`tabpanel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
        tabIndex={0}
      >
        {renderTabContent()}
      </div>
    </div>
  );
}
